import React from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Calendar, AlertTriangle, TrendingUp } from 'lucide-react'
import { getRiskColor, getAdherenceStatus } from '../config/theme'
import './PatientList.css'

function PatientList({ patients }) {
  const navigate = useNavigate()

  const handlePatientClick = (patientId) => {
    navigate(`/patients/${patientId}`)
  }

  if (!patients || patients.length === 0) {
    return (
      <div className="patient-list-empty">
        <User size={48} className="empty-icon" />
        <p>No patients found</p>
      </div>
    )
  }

  return (
    <div className="patient-list">
      <div className="patient-list-header">
        <h2>Patients ({patients.length})</h2>
      </div>
      
      <div className="patient-cards">
        {patients.map((patient) => {
          const adherenceStatus = getAdherenceStatus(patient.medication_adherence || 0)
          const riskColor = getRiskColor(patient.risk_level)
          
          return (
            <div
              key={patient.patient_id}
              className="patient-card"
              onClick={() => handlePatientClick(patient.patient_id)}
            >
              <div className="patient-card-header">
                <div className="patient-info">
                  <div className="patient-avatar" style={{ borderColor: riskColor }}>
                    <User size={24} />
                  </div>
                  <div>
                    <h3 className="patient-name">
                      {patient.first_name} {patient.last_name}
                    </h3>
                    <p className="patient-id">{patient.patient_id} • Age {patient.age}</p>
                  </div>
                </div>
                
                <div className="patient-risk">
                  <span 
                    className={`risk-badge risk-${patient.risk_level}`}
                    style={{ backgroundColor: riskColor }}
                  >
                    {patient.risk_level?.toUpperCase()}
                  </span>
                  <span className="risk-score">{patient.risk_score}/100</span>
                </div>
              </div>

              <div className="patient-card-body">
                {patient.chronic_conditions && patient.chronic_conditions.length > 0 && (
                  <div className="patient-conditions">
                    {patient.chronic_conditions.map((condition, idx) => (
                      <span key={idx} className="condition-tag">
                        {condition}
                      </span>
                    ))}
                  </div>
                )}

                <div className="patient-metrics">
                  <div className="metric">
                    <div className="metric-icon" style={{ color: adherenceStatus.color }}>
                      <TrendingUp size={16} />
                    </div>
                    <div className="metric-details">
                      <span className="metric-label">Medication Adherence</span>
                      <span className="metric-value" style={{ color: adherenceStatus.color }}>
                        {patient.medication_adherence}% - {adherenceStatus.label}
                      </span>
                    </div>
                  </div>

                  {patient.active_alerts_count > 0 && (
                    <div className="metric">
                      <div className="metric-icon alert-icon">
                        <AlertTriangle size={16} />
                      </div>
                      <div className="metric-details">
                        <span className="metric-label">Active Alerts</span>
                        <span className="metric-value alert-value">
                          {patient.active_alerts_count} alert{patient.active_alerts_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  )}

                  {patient.next_appointment && (
                    <div className="metric">
                      <div className="metric-icon">
                        <Calendar size={16} />
                      </div>
                      <div className="metric-details">
                        <span className="metric-label">Next Appointment</span>
                        <span className="metric-value">
                          {new Date(patient.next_appointment.date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="patient-card-footer">
                <span className="patient-physician">
                  Dr. {patient.primary_physician}
                </span>
                {patient.last_visit && (
                  <span className="patient-last-visit">
                    Last visit: {new Date(patient.last_visit).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PatientList